import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/articles';


const orgSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '写作技巧大全',
  url: 'https://zuowenjiqiao.com',
  description:
    '专注中小学生作文写作指导，提供优质范文、专业写作技巧与丰富素材，帮助孩子提升语文写作能力。',
});

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9qaoj4nlsngrm4lluog',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (
        response.ok &&
        parsed &&
        (parsed.code === 'OK' || parsed.code === 'ok')
      ) {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <title>关于我们 - 写作技巧大全</title>
      <meta
        name="description"
        content="写作技巧大全致力于为中小学生和家长提供优质作文范文与专业写作技巧指导。了解我们的品牌故事、用户口碑与联系方式。"
      />
      <meta
        name="keywords"
        content="写作技巧大全,关于我们,作文指导,联系我们,作文网站"
      />
      <link rel="canonical" href="https://zuowenjiqiao.com/about" />
      <meta property="og:title" content="关于我们 - 写作技巧大全" />
      <meta
        property="og:description"
        content="了解写作技巧大全的品牌故事、用户口碑与联系方式。"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://zuowenjiqiao.com/about" />
      <script type="application/ld+json">{orgSchema}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            <section className="mb-12">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-6">
                关于写作技巧大全
              </h1>
              <div className="w-full aspect-[21/9] rounded-lg overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20Chinese%20library%20or%20study%20space%20with%20bookshelves%20filled%20with%20Chinese%20books%2C%20sunlight%20streaming%20through%20windows%2C%20wooden%20reading%20tables%2C%20peaceful%20and%20scholarly%20atmosphere%2C%20soft%20natural%20tones%2C%20educational%20environment&width=1600&height=700&seq=about-hero-01&orientation=landscape"
                  alt="写作技巧大全 - 温暖的阅读空间"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-5">
                  <div>
                    <h2 className="text-xl font-heading text-foreground-900 mb-3">
                      品牌故事
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      写作技巧大全诞生于一群语文教育工作者和热爱写作的家长的共同愿景。我们发现，
                      很多中小学生在面对"成长经历"和"家庭教育"类作文时，常常感到无从下笔——
                      要么不知道写什么，要么写出来的是空洞的流水账，缺乏真情实感。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-3">
                      于是，我们决定创建一个专门针对这类主题的写作指导平台。我们精心筛选优秀范文，
                      从叙事结构、情感表达、细节描写等多个维度拆解写作方法，同时提供丰富的写作素材和思路引导，
                      让孩子在阅读中学会写作，在写作中感悟成长。
                    </p>
                  </div>

                  <div>
                    <h2 className="text-xl font-heading text-foreground-900 mb-3">
                      我们的使命
                    </h2>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      我们相信，每个孩子都有自己独特的成长故事。我们的使命是帮助孩子把这些故事写好、
                      写活、写出真情实感。通过优质的范文引导和系统的写作技巧训练，让作文不再成为孩子的负担，
                      而是表达自我、记录成长的美好方式。
                    </p>
                  </div>

                  <div>
                    <h2 className="text-xl font-heading text-foreground-900 mb-3">
                      内容特色
                    </h2>
                    <ul className="space-y-2">
                      {[
                        '精选优质范文，主题涵盖成长经历与家庭教育',
                        '系统讲解叙事技巧、情感表达、细节描写等核心写作方法',
                        '提供丰富的写作素材和思路引导，启发孩子独立思考',
                        '所有内容均免费开放，方便学生和家长随时查阅',
                        '每周更新内容，持续提供新鲜的写作指导',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm md:text-base text-foreground-700">
                          <i className="ri-check-line text-accent-500 flex-shrink-0 mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70 self-start">
                  <h3 className="text-lg font-heading text-foreground-900 mb-4">站内导航</h3>
                  <ul className="space-y-3">
                    {[
                      { label: '范文精选', href: '/category/fanwen-jingxuan', icon: 'ri-article-line' },
                      { label: '叙事技巧', href: '/category/xushi-jiqiao', icon: 'ri-quill-pen-line' },
                      { label: '情感表达', href: '/category/qinggan-biaoda', icon: 'ri-heart-line' },
                      { label: '细节描写', href: '/category/xijie-miaoxie', icon: 'ri-focus-2-line' },
                      { label: '写作思路', href: '/category/xiezuo-silu', icon: 'ri-lightbulb-line' },
                    ].map((item) => (
                      <li key={item.href}>
                        <a
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            window.REACT_APP_NAVIGATE(item.href);
                          }}
                          className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                        >
                          <i className={`${item.icon} text-accent-500`}></i>
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1.5 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{t.content}"
                    </p>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact-form">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
                联系我们
              </h2>
              <div className="max-w-lg mx-auto bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <p className="text-sm text-foreground-600 mb-5 text-center">
                  如果您有任何建议或问题，欢迎通过以下表单与我们联系。
                </p>

                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      留言内容 <span className="text-accent-600">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="spam-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    提交留言
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-green-600">
                      感谢您的留言，我们会尽快回复！
                    </p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-center text-accent-600">{formError}</p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}